// Deck theme catalog + preview thumbnails (the bundled house styles).
//
// Mirrors the backend `src/railyard/routes/decks.py`. The preview endpoint is
// unauthenticated (generic brand specimens, no tenant data), so the thumbnail
// URL is safe in a plain <img src> — no authed-blob fetch needed.

import type { components } from "@/lib/api-types";

import { API, requestJson } from "../api";

type Schemas = components["schemas"];

export type DeckTheme = Schemas["DeckTheme"];
export type FirmThemeStatus = Schemas["FirmThemeStatus"];
export type FirmSampleDeckStatus = Schemas["FirmSampleDeckStatus"];

/** The selectable deck house styles (default + the OOB themes), with a preview
 *  URL for each. Consumed by the house-style picker. */
export async function listDeckThemes(opts?: { signal?: AbortSignal }): Promise<DeckTheme[]> {
  return requestJson(`${API}/api/decks/themes`, { signal: opts?.signal });
}

/** Absolute URL for a theme's deterministic specimen PNG. */
export function deckThemePreviewUrl(name: string): string {
  return `${API}/api/decks/themes/${encodeURIComponent(name)}/preview`;
}

/** Whether the active workspace has a deck-relevant firm brand. Tenant-scoped —
 *  drives whether the deck surfaces show the firm specimen or the generic Default. */
export async function firmThemeStatus(opts?: { signal?: AbortSignal }): Promise<FirmThemeStatus> {
  return requestJson(`${API}/api/decks/themes/firm`, { signal: opts?.signal });
}

/** Absolute URL for the active workspace's firm specimen PNG. Unlike the OOB
 *  previews this is tenant-private + authed — fetch it via the authed-blob path
 *  (`useAuthedBlobUrl`), not a plain <img src>. Falls back to the Default specimen
 *  server-side on a cache miss / no firm theme, so it always yields a valid image. */
export function deckFirmPreviewUrl(): string {
  return `${API}/api/decks/themes/firm/preview`;
}

/** Readiness of the active workspace's branded full sample CIM (ENG-4850).
 *  Tenant-scoped + authed. `source` is `firm` only when the cache holds this
 *  workspace's brand; a cold miss serves the Default gallery and is labelled
 *  `neutral` — never treat those bytes as the firm brand. */
export async function firmSampleDeckStatus(
  opts?: { signal?: AbortSignal },
): Promise<FirmSampleDeckStatus> {
  return requestJson(`${API}/api/decks/themes/firm/sample`, { signal: opts?.signal });
}

export function firmSampleDeckPptxUrl(): string {
  return `${API}/api/decks/themes/firm/sample/pptx`;
}

export function firmSampleDeckPdfUrl(): string {
  return `${API}/api/decks/themes/firm/sample/pdf`;
}

export function firmSampleDeckPageUrl(n: number): string {
  return `${API}/api/decks/themes/firm/sample/pages/${n}`;
}
